import { useCallback, useMemo } from 'react';
import { format } from 'date-fns';
import { TrashIcon, XIcon } from 'lucide-react';
import { toast } from 'sonner';

import { CustomerPreferencesDays } from '@/app/(app)/customers/[id]/customer-preferences-days';
import { useCustomerCtx } from '@/app/(app)/customers/[id]/provider';
import { Popover } from '@/components/atoms/popover';
import { SearchSingleSelect } from '@/components/atoms/select';
import { EmployeeAvatar } from '@/components/molecules/employee-avatar';
import { useActiveEmployees } from '@/components/providers/employee-provider';
import { ApiSchemas } from '@/components/providers/v1-api';
import { useEmployeeById } from '@/components/providers/v1-office-hooks';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { createUUID } from '@/lib/uuid';

type ApiPreferences = ApiSchemas['CustomerPreferences-Input'];

export function CustomerPreferences() {
  const { customer, updateCustomer } = useCustomerCtx();

  const onChange = useCallback(
    (update: Partial<typeof customer.preferences>) => {
      updateCustomer({ preferences: { ...customer.preferences, ...update } });
    },
    [updateCustomer, customer]
  );

  return (
    <div className='flex w-full flex-col gap-8'>
      <TechnicianPreferences
        technicians={customer.preferences.technicians}
        update={(technicians) => onChange({ technicians })}
      />
      <CustomerPreferencesDays days={customer.preferences.days} update={(days) => onChange({ days })} />
      <BlockedDatePreferences days={customer.preferences.dates} update={(dates) => onChange({ dates })} />
    </div>
  );
}

interface TechnicianPreferencesProps {
  technicians: ApiPreferences['technicians'];
  update: (technicians: ApiPreferences['technicians']) => void;
}

function TechnicianPreferences({ technicians, update }: TechnicianPreferencesProps) {
  const activeEmployees = useActiveEmployees({ sortBy: 'first_name' });
  const options = useMemo(() => {
    return activeEmployees
      .filter((employee) => employee.bookable)
      .map((employee) => ({
        label: `${employee.first_name} ${employee.last_name}`,
        value: employee.id,
      }));
  }, [activeEmployees]);
  const requiredTechnicians = technicians.filter((t) => t.preference === 'REQUIRED');
  const blockedTechnicians = technicians.filter((t) => t.preference === 'BLOCKED');

  return (
    <>
      <div className='flex w-full flex-col gap-4'>
        <h3 className='text-lg font-medium'>Required technicians</h3>
        {requiredTechnicians.length > 0 && (
          <div className='flex flex-col gap-2'>
            {requiredTechnicians.map((technician) => (
              <TechnicianCard
                key={technician.employee_id}
                employeeId={technician.employee_id}
                onRemove={() => update(technicians.filter((t) => t.employee_id !== technician.employee_id))}
              />
            ))}
          </div>
        )}
        <SearchSingleSelect
          trigger={
            <Button variant='link' size='sm' className='h-fit w-fit px-0 font-medium'>
              Add a required technician
            </Button>
          }
          selected=''
          onSelect={(value) => {
            if (technicians.some((t) => t.employee_id === value)) {
              toast.error('Technician already added');
            } else {
              update([...technicians, { preference: 'REQUIRED', employee_id: value }]);
            }
          }}
          options={options}
          align='start'
        />
      </div>
      <div className='flex w-full flex-col gap-4'>
        <h3 className='text-lg font-medium'>Blocked technicians</h3>
        {blockedTechnicians.length > 0 && (
          <div className='flex flex-col gap-2'>
            {blockedTechnicians.map((technician) => (
              <TechnicianCard
                key={technician.employee_id}
                employeeId={technician.employee_id}
                onRemove={() => update(technicians.filter((t) => t.employee_id !== technician.employee_id))}
              />
            ))}
          </div>
        )}
        <SearchSingleSelect
          trigger={
            <Button variant='link' size='sm' className='h-fit w-fit px-0 font-medium'>
              Add a blocked technician
            </Button>
          }
          selected=''
          onSelect={(value) => {
            if (technicians.some((t) => t.employee_id === value)) {
              toast.error('Technician already added');
            } else {
              update([...technicians, { preference: 'BLOCKED', employee_id: value }]);
            }
          }}
          options={options}
          align='start'
        />
      </div>
    </>
  );
}

interface TechnicianCardProps {
  employeeId: string;
  onRemove: () => void;
}

function TechnicianCard({ employeeId, onRemove }: TechnicianCardProps) {
  return (
    <div className='flex w-full items-center gap-2 rounded-3xl p-3 shadow-md'>
      <EmployeeAvatar employeeId={employeeId} />
      <div className='text-sm leading-none'>
        <EmployeeName id={employeeId} />
      </div>
      <Button variant='outline' size='icon' onClick={onRemove} className='ml-auto'>
        <TrashIcon className='h-4 w-4' />
      </Button>
    </div>
  );
}

function EmployeeName({ id }: { id: string }) {
  const employee = useEmployeeById(id);
  return (
    <>
      {employee?.first_name} {employee?.last_name}
    </>
  );
}

interface BlockedDayPreferencesProps {
  days: ApiPreferences['dates'];
  update: (days: ApiPreferences['dates']) => void;
}

function BlockedDatePreferences({ days, update }: BlockedDayPreferencesProps) {
  return (
    <div className='flex w-full flex-col gap-4'>
      <h3 className='text-lg font-medium'>Blocked Dates</h3>
      {days.length > 0 && (
        <div className='flex flex-wrap items-center gap-3'>
          {days.map((day) => (
            <div
              key={day.id}
              className='flex items-center gap-2 rounded-md bg-secondary px-1.5 py-0.5 text-xs text-secondary-foreground'
            >
              <span>{format(new Date(day.date), 'MMM d, yyyy')}</span>
              <button onClick={() => update(days.filter((d) => d.id !== day.id))}>
                <XIcon className='h-4 w-4' />
              </button>
            </div>
          ))}
        </div>
      )}
      <Popover
        align='start'
        trigger={
          <Button variant='link' size='sm' className='h-fit w-fit px-0 font-medium'>
            Add a blocked date
          </Button>
        }
      >
        <Calendar
          mode='single'
          onSelect={(date) => {
            if (date) {
              update([...days, { id: createUUID(), date: date.toISOString(), preference: 'BLOCKED' }]);
            }
          }}
        />
      </Popover>
    </div>
  );
}
